import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import NotificationBell from '../notifications/NotificationBell';
import {
  getNavigationLinks,
  flattenNavigationQuickLinks,
  ADMIN_DASHBOARD_FEATURED_PATHS
} from '../../config/navigationConfig';

/**
 * Dashboard Admin: solo strumenti rapidi (stesse voci del menu laterale), senza KPI o numeri.
 */
const AdminDashboard = () => {
  const { currentUser, isAdmin, isGym, isTrainer } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { featuredLinks, groupedLinks } = useMemo(() => {
    const { mainLinks, secondaryLinks } = getNavigationLinks(
      currentUser,
      isGym,
      isTrainer,
      isAdmin
    );
    const all = flattenNavigationQuickLinks([...mainLinks, ...secondaryLinks], {
      isAdmin: isAdmin()
    });
    const featuredSet = new Set(ADMIN_DASHBOARD_FEATURED_PATHS);
    const featured = ADMIN_DASHBOARD_FEATURED_PATHS.map((to) => all.find((l) => l.to === to)).filter(Boolean);
    const rest = all.filter((l) => !featuredSet.has(l.to));

    const groups = new Map();
    for (const link of rest) {
      const key = link.groupLabel || '__root__';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(link);
    }

    return { featuredLinks: featured, groupedLinks: groups };
  }, [currentUser, isAdmin, isGym, isTrainer]);

  const renderToolCard = (link, prominent = false) => {
    const Icon = link.icon;
    return (
      <button
        key={link.to}
        type="button"
        onClick={() => navigate(link.to)}
        className={`group flex items-center gap-4 p-4 rounded-xl border-2 text-left w-full transition-all bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md ${
          prominent ? 'md:p-5' : ''
        }`}
      >
        <div
          className={`flex-shrink-0 rounded-xl flex items-center justify-center ${
            prominent ? 'h-12 w-12' : 'h-10 w-10'
          } bg-blue-500/10 text-blue-600 dark:text-blue-400`}
        >
          <Icon className={prominent ? 'h-6 w-6' : 'h-5 w-5'} />
        </div>
        <div className="flex-1 min-w-0">
          <span
            className={`font-semibold text-gray-900 dark:text-white block ${
              prominent ? 'text-base' : 'text-sm'
            }`}
          >
            {t(link.label)}
          </span>
        </div>
        <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 flex-shrink-0" />
      </button>
    );
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {isAdmin() ? t('admin.panel.title') : t('admin.panel.trainerTitle')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">{t('admin.dashboard.subtitle')}</p>
        </div>
        <NotificationBell showPopup={true} popupDuration={5000} />
      </div>

      {featuredLinks.length > 0 && (
        <section className="mb-10">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
            {t('admin.dashboard.frequentTools')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {featuredLinks.map((link) => renderToolCard(link, true))}
          </div>
        </section>
      )}

      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
          {t('admin.dashboard.allTools')}
        </h2>
        <div className="space-y-8">
          {Array.from(groupedLinks.entries()).map(([groupKey, links]) => (
            <div key={groupKey}>
              {groupKey !== '__root__' && (
                <h3 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-1">
                  {t(groupKey)}
                </h3>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {links.map((link) => renderToolCard(link))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
